/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import { MobileSearchBarTab } from '@eliadmin/core/src/components/header/search-bar/mobile-search-bar-tab';
import { SearchBarSidebar } from '@eliadmin/core/src/components/header/search-bar/search-bar-sidebar';
import { Button } from '@eliadmin/core/src/components/ui/button';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@eliadmin/core/src/components/ui/popover';
import { SheetTrigger } from '@eliadmin/core/src/components/ui/sheet';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { Search as LuSearch } from 'lucide-react';

export type SearchBarProps = {
  setSearchQuery: (query: string) => void;
  searchQuery: string;
  isLoading: boolean;
  shouldRedirect?: boolean;
  items: React.ReactNode[];
  totalProducts?: number;
  totalBlogs?: number;
  totalTags?: number;
  totalCategories?: number;
  itemsBlogs?: Blog[];
  itemsTags?: any[];
  itemsCategories?: any[];
  placeholder?: string;
  customButton?: React.ReactNode;
  desktopSearchButton?: boolean;
  mobileSearchButton?: boolean;
  disableSidebar?: boolean;
  className?: {
    root?: string;
    form?: string;
    input?: InputProps['className'];
    button?: string;
    popoverContent?: string;
    spinner?: string;
    itemsClassName?: string;
    itemClassName?: string;
    empty?: string;
    emptyText?: string;
  };
  toggle?: () => void;
};

export function BlogSearchBarInner({
  className,
  setSearchQuery,
  searchQuery,
  isLoading,
  items,
  totalProducts,
  totalBlogs,
  totalTags,
  totalCategories,
  itemsBlogs,
  itemsTags,
  itemsCategories,
  placeholder,
  shouldRedirect = false,
  customButton,
  mobileSearchButton = false,
  desktopSearchButton = false,
  disableSidebar = false,
  toggle,
}: SearchBarProps) {
  const { t } = useTranslations();
  const router = useRouter();
  const pathname = usePathname();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement> | boolean = false) => {
    if (typeof e === 'object') {
      e.preventDefault();
    }

    if (!searchQuery || searchQuery === '') {
      router.push(shouldRedirect ? ROUTES.SEARCH() : `${pathname}`);
      return;
    }

    if (toggle) {
      toggle();
    }

    router.push(shouldRedirect ? ROUTES.SEARCH(searchQuery) : `${pathname}?search=${searchQuery}`);
  };

  const formRef = useRef<HTMLFormElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [shouldClose, setShouldClose] = useState(false);

  useEffect(() => {
    setIsOpen(false);
    setShouldClose(false);
  }, [pathname]);

  function handleOpenChange(open: boolean) {
    if (open) {
      setShouldClose(false);
      setIsOpen(true);
    } else if (shouldClose) {
      setShouldClose(false);
      setIsOpen(false);
    }
  }

  const tabsTriggerClassName = cn(
    'mr-1 h-full font-semibold rounded-none rounded-t-sm bg-transparent shadow-none text-xs transition-none',
    'text-[hsl(var(--primary))]',
    'data-[state=active]:text-black data-[state=active]:border-none data-[state=active]:shadow-none',
  );

  return (
    <Popover
      open={(mobileSearchButton || isOpen) && searchQuery.length > 1}
      onOpenChange={handleOpenChange}
    >
      <PopoverAnchor
        asChild
        className={cn('w-full', 'ea-search-bar', className?.root)}
      >
        <form
          role="search"
          aria-label={t('text590')}
          onFocusCapture={() => setIsOpen(true)}
          ref={formRef}
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
            toggle && toggle();
          }}
          className={cn('flex w-full flex-row items-center', 'ea-blog-search-bar-inner__form')}
        >
          <Input
            aria-label={t('text590')}
            before={<FiSearch className="ml-2 h-4 w-4 text-muted-foreground" />}
            value={searchQuery}
            placeholder={placeholder ?? t('text162')}
            onChange={e => {
              setSearchQuery(e.target.value);
              setIsOpen(true);
            }}
            className={{
              ...className?.input,
              wrapper: cn(
                'border-e-none h-12 rounded-lg pr-1',
                'ea-search-bar__input',
                className?.input?.wrapper,
              ),
            }}
            after={
              customButton || (
                <Button
                  aria-label={t('text590')}
                  variant={HEADER_CONFIG.searchBar.buttonVariant}
                  className={cn(
                    'h-10 w-10 rounded-lg border-none p-0',
                    'ea-search-bar__button',
                    className?.button,
                  )}
                >
                  <LuSearch className="h-5 w-5" />
                </Button>
              )
            }
          />
        </form>
      </PopoverAnchor>
      <PopoverContent
        alignOffset={50}
        onOpenAutoFocus={e => e.preventDefault()}
        className={cn(
          'hidden w-full px-0 sm:block sm:max-w-[730px] md:max-w-[384px]',
          'ea-blog-search-bar-inner__popover-content',
          className?.popoverContent,
        )}
        ref={contentRef}
        onPointerDownOutside={() => {
          setShouldClose(true);
        }}
      >
        <div className="flex gap-4">
          {items && (
            <div className="w-full">
              {isLoading ? (
                <div
                  className={cn(
                    'flex h-[300px] w-full items-center justify-center',
                    'ea-search-bar__spinner',
                    className?.spinner,
                  )}
                >
                  <Spinner />
                </div>
              ) : items.length ? (
                <div
                  className={cn(
                    'flex w-full flex-col',
                    'ea-search-bar__items',
                    className?.itemsClassName,
                  )}
                >
                  {items.map(
                    item =>
                      item &&
                      React.cloneElement(item as any, {
                        className: cn('', className?.itemClassName),
                      }),
                  )}
                  {desktopSearchButton && (
                    // <SheetClose asChild>
                    <div className="flex w-full flex-row justify-center px-4">
                      <Button
                        variant={'outline'}
                        type="button"
                        onClick={(e: any) => handleSubmit(e)}
                        className={cn(
                          'h-10 w-full rounded-lg lg:w-auto',
                          'ea-search-bar__button',
                          className?.button,
                        )}
                      >
                        {t('text670')}
                      </Button>
                    </div>
                    // </SheetClose>
                  )}
                </div>
              ) : (
                <div className={cn('flex flex-col p-4', 'ea-search-bar__empty', className?.empty)}>
                  <p className={cn('text-sm', 'ea-search-bar__empty-text', className?.emptyText)}>
                    {t('text554')}
                  </p>
                </div>
              )}
            </div>
          )}
          <div className="flex w-[1px] py-4">
            <div className="w-[2px] bg-border"></div>
          </div>
          {!disableSidebar && (
            <div className="w-[340px]">
              <SearchBarSidebar blogs={itemsBlogs} tags={itemsTags} categories={itemsCategories} />
            </div>
          )}
        </div>
      </PopoverContent>
      <PopoverContent
        onOpenAutoFocus={e => e.preventDefault()}
        className={cn(
          'max-h-[500px] w-full max-w-[100vw] overflow-y-scroll px-0 sm:hidden',
          'ea-search-bar__popover-content',
          className?.popoverContent,
        )}
        ref={contentRef}
      >
        <Tabs defaultValue="products">
          <TabsList
            className={cn(
              'hide-scroll-bar box-border justify-start overflow-visible rounded-none bg-transparent',
              'ea-search-bar-tabs__list',
            )}
          >
            <TabsTrigger value="products" className={cn(tabsTriggerClassName)}>
              {t('text150')} ({totalProducts || 0})
            </TabsTrigger>
            <TabsTrigger value="blogs" className={cn(tabsTriggerClassName)}>
              {t('text671')} ({totalBlogs || 0})
            </TabsTrigger>
            <TabsTrigger value="tags" className={cn(tabsTriggerClassName)}>
              {t('text437')} ({totalTags || 0})
            </TabsTrigger>
            <TabsTrigger value="categories" className={cn(tabsTriggerClassName)}>
              {t('text176')} ({totalCategories || 0})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="products" className="flex gap-4">
            {items && (
              <div className="w-full">
                {isLoading ? (
                  <div
                    className={cn(
                      'flex h-[300px] w-full items-center justify-center',
                      'ea-search-bar__spinner',
                      className?.spinner,
                    )}
                  >
                    <Spinner />
                  </div>
                ) : items.length ? (
                  <div
                    className={cn(
                      'flex w-full flex-col',
                      'ea-search-bar__items',
                      'ea-blog-search-bar-inner__items-mobile',
                      className?.itemsClassName,
                    )}
                  >
                    {items.map(
                      item =>
                        item &&
                        React.cloneElement(item as any, {
                          className: cn('', className?.itemClassName),
                        }),
                    )}
                    {mobileSearchButton && (
                      <SheetTrigger asChild>
                        <Button
                          variant={'outline'}
                          type="button"
                          onClick={(e: any) => handleSubmit(e)}
                          className={cn(
                            'h-10 w-full rounded-lg p-0',
                            'ea-search-bar__button',
                            className?.button,
                          )}
                        >
                          {t('text670')}
                        </Button>
                      </SheetTrigger>
                    )}
                  </div>
                ) : (
                  <div
                    className={cn('flex flex-col p-4', 'ea-search-bar__empty', className?.empty)}
                  >
                    <p className={cn('text-sm', 'ea-search-bar__empty-text', className?.emptyText)}>
                      {t('text554')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
          <TabsContent value="blogs">
            <MobileSearchBarTab type={'blogs'} items={itemsBlogs} toggle={toggle} />
          </TabsContent>
          <TabsContent value="tags">
            <MobileSearchBarTab type={'tags'} items={itemsTags} toggle={toggle} />
          </TabsContent>
          <TabsContent value="categories">
            <MobileSearchBarTab type={'categories'} items={itemsCategories} toggle={toggle} />
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
